import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { heroApi } from "../api/hero";
import { useGameStore } from "../store/useGameStore";
import { SkillIcon } from "../components/SkillIcon";
import { SkillUpgradeModal } from "../components/SkillUpgradeModal";
import { PassiveUpgradeModal } from "../components/PassiveUpgradeModal";
import { resolveSkillDisplay } from "../utils/assets";
import type { PassiveTreeEntry, PassiveTreeResponse, SkillTreeEntry, SkillTreeResponse } from "../types";

type Tab = "active" | "passives";

export function SkillTreeScreen() {
  const { hero, balance, setScreen, setSkillNames, refreshHero, refreshBalance } = useGameStore();
  const [tab, setTab] = useState<Tab>("active");
  const [viewBranch, setViewBranch] = useState(0);
  const [tree, setTree] = useState<SkillTreeResponse | null>(null);
  const [passiveTree, setPassiveTree] = useState<PassiveTreeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<SkillTreeEntry | null>(null);
  const [selectedPassive, setSelectedPassive] = useState<PassiveTreeEntry | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const cacheSkillNames = useCallback((data: SkillTreeResponse) => {
    const names: Record<string, string> = { ...(data.skill_names ?? {}) };
    for (const entry of data.skills) names[entry.skill_name] = entry.display_name;
    setSkillNames(names);
  }, [setSkillNames]);

  const loadActive = useCallback(async (branch: number) => {
    const data = await heroApi.getSkillTree(branch);
    setTree(data);
    cacheSkillNames(data);
    return data;
  }, [cacheSkillNames]);

  const loadPassives = useCallback(async (branch: number) => {
    const data = await heroApi.getPassiveTree(branch);
    setPassiveTree(data);
    return data;
  }, []);

  const loadAll = useCallback(async (branch: number, options?: { silent?: boolean }) => {
    if (!options?.silent) {
      setLoading(true);
    }
    setError(null);
    try {
      await Promise.all([loadActive(branch), loadPassives(branch)]);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load skill tree");
    } finally {
      if (!options?.silent) {
        setLoading(false);
      }
    }
  }, [loadActive, loadPassives]);

  useEffect(() => {
    const initial = hero?.active_skill_branch ?? 0;
    setViewBranch(initial);
    void loadAll(initial);
  }, [hero?.active_skill_branch, loadAll]);

  const switchViewBranch = async (branch: number) => {
    if (branch === viewBranch) return;
    setViewBranch(branch);
    setSelected(null);
    setSelectedPassive(null);
    const unlocked = hero?.skill_branch_unlocked ?? tree?.branch_unlocked;
    if (branch === 1 && !unlocked) {
      setTree(null);
      setPassiveTree(null);
      setLoading(false);
      return;
    }
    await loadAll(branch);
  };

  const handleSelectCombatBranch = async () => {
    if (!tree || viewBranch === tree.active_branch) return;
    setActionLoading(true);
    setError(null);
    try {
      await heroApi.selectSkillBranch(viewBranch);
      await Promise.all([refreshHero(), refreshBalance()]);
      await loadAll(viewBranch);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to switch branch");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUnlockBranch = async () => {
    if (!confirm(`Unlock Branch II for ${tree?.branch_unlock_cost_diamonds ?? 100} diamonds?`)) return;
    setActionLoading(true);
    setError(null);
    try {
      await heroApi.unlockSkillBranch();
      await Promise.all([refreshHero(), refreshBalance()]);
      await loadAll(viewBranch);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unlock failed");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReset = async () => {
    const cost = tree?.reset_cost_diamonds ?? 50;
    if (!confirm(`Reset all skill and passive upgrades for ${cost} diamonds? Skill points will be refunded.`)) {
      return;
    }
    setActionLoading(true);
    setError(null);
    try {
      await heroApi.resetSkillBuild();
      await Promise.all([refreshHero(), refreshBalance()]);
      await loadAll(viewBranch);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Reset failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (!hero) {
    return <div className="p-4 text-gray-400 text-center py-12">Loading hero...</div>;
  }

  const nextLocked = (entry: SkillTreeEntry) => {
    if (entry.current_level >= entry.max_level) return false;
    const next = entry.levels[entry.current_level];
    return !next?.unlocked;
  };

  const passiveLocked = (entry: PassiveTreeEntry) => {
    if (entry.current_level >= entry.max_level) return false;
    const next = entry.levels[entry.current_level];
    return !next?.unlocked;
  };

  const branchSpSource = tab === "passives" ? passiveTree ?? tree : tree ?? passiveTree;
  const skillPoints = branchSpSource?.skill_points ?? hero.skill_points;
  const spCostPerUpgrade = branchSpSource?.sp_cost_per_upgrade ?? 1;
  const heroMaxLevel = branchSpSource?.hero_max_level ?? 80;
  const spEffectiveLevel =
    branchSpSource?.skill_points_effective_level ?? Math.min(hero.level, heroMaxLevel);
  const skillPointsEarned =
    branchSpSource?.skill_points_earned ??
    Math.floor(spEffectiveLevel / 4) + Math.floor((14 * spEffectiveLevel) / heroMaxLevel);
  const branchUnlocked = tree?.branch_unlocked ?? passiveTree?.branch_unlocked ?? hero.skill_branch_unlocked;
  const activeBranch = tree?.active_branch ?? passiveTree?.active_branch ?? hero.active_skill_branch ?? 0;
  const branchLocked = viewBranch === 1 && !branchUnlocked;
  const needsCombatSelect = viewBranch !== activeBranch && !branchLocked;

  return (
    <div className="screen-root p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button
          onClick={() => setScreen("hero")}
          className="text-kingdom-glow text-sm font-medium"
        >
          ← Back
        </button>
        <h2 className="text-xl font-bold font-game text-gold-400 flex-1 text-center">🌳 Skill Tree</h2>
        <div className="w-12" />
      </div>

      <div className="flex rounded-xl border border-kingdom-mid overflow-hidden">
        {([0, 1] as const).map((b) => (
          <button
            key={b}
            type="button"
            disabled={actionLoading}
            onClick={() => void switchViewBranch(b)}
            className={clsx(
              "flex-1 py-2.5 text-sm font-bold transition-colors relative",
              viewBranch === b
                ? "bg-kingdom-accent text-white"
                : "bg-kingdom-dark text-gray-400 hover:text-kingdom-glow",
            )}
          >
            Branch {b === 0 ? "I" : "II"}
            {activeBranch === b && (
              <span className="block text-[10px] font-normal opacity-90">In combat</span>
            )}
          </button>
        ))}
      </div>

      {needsCombatSelect && (
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => void handleSelectCombatBranch()}
          className="w-full py-2 rounded-xl border border-kingdom-glow text-kingdom-glow text-sm font-bold"
        >
          Use Branch {viewBranch === 0 ? "I" : "II"} in combat
        </button>
      )}

      <div className="flex gap-2">
        <button
          type="button"
          disabled={actionLoading || branchUnlocked}
          onClick={() => void handleUnlockBranch()}
          className={clsx(
            "flex-1 py-2 rounded-xl text-xs font-bold border",
            branchUnlocked
              ? "border-gray-700 text-gray-500"
              : "border-amber-500/50 text-amber-300",
          )}
        >
          {branchUnlocked ? "Branch II unlocked" : `Unlock Branch II — ${tree?.branch_unlock_cost_diamonds ?? 100} 💎`}
        </button>
        <button
          type="button"
          disabled={actionLoading}
          onClick={() => void handleReset()}
          className="flex-1 py-2 rounded-xl text-xs font-bold border border-rose-500/40 text-rose-300"
        >
          Reset — {tree?.reset_cost_diamonds ?? 50} 💎
        </button>
      </div>

      <div className="flex rounded-xl border border-kingdom-mid overflow-hidden">
        {(["active", "passives"] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              "flex-1 py-2.5 text-sm font-bold transition-colors",
              tab === t
                ? "bg-kingdom-accent text-white"
                : "bg-kingdom-dark text-gray-400 hover:text-kingdom-glow",
            )}
          >
            {t === "active" ? "Active" : "Passives"}
          </button>
        ))}
      </div>

      <div className="bg-kingdom-purple rounded-xl border border-kingdom-mid p-3 flex items-center justify-between">
        <div className="text-gray-400 text-sm">
          Skill points
          <span className="block text-[10px] text-gray-500">
            Branch {viewBranch === 0 ? "I" : "II"} · {skillPoints} available · {skillPointsEarned}{" "}
            total budget (Lv.{spEffectiveLevel} of {heroMaxLevel})
          </span>
        </div>
        <span className="text-kingdom-glow font-bold text-lg">{skillPoints}</span>
      </div>

      {loading && <div className="text-center text-gray-400 py-8">Loading...</div>}
      {error && (
        <div className="bg-rose-900/30 border border-rose-500 rounded-xl p-3 text-rose-300 text-sm">
          {error}
        </div>
      )}

      {!loading && branchLocked && (
        <div className="relative rounded-xl border border-kingdom-mid bg-kingdom-dark/80 p-8 text-center space-y-4">
          <p className="text-gray-300 text-sm">Branch II is locked. Unlock it to train an alternate build.</p>
          <button
            type="button"
            disabled={actionLoading}
            onClick={() => void handleUnlockBranch()}
            className="px-6 py-3 rounded-xl bg-gradient-to-r from-kingdom-accent to-kingdom-glow text-white font-bold"
          >
            Unlock for {tree?.branch_unlock_cost_diamonds ?? 100} 💎
          </button>
        </div>
      )}

      {!loading && !branchLocked && tab === "active" && tree && (
        <>
          <div className="skill-tree-grid grid grid-cols-4 gap-x-1 gap-y-3 py-2 px-0.5 justify-items-center">
            {tree.skills.map((entry) => (
              <SkillNode
                key={entry.skill_name}
                entry={entry}
                heroClass={hero.hero_class}
                locked={nextLocked(entry)}
                onSelect={() => setSelected(entry)}
              />
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 px-4">
            Main path: first skill → second → ultimate → fourth. Side branches open from your second skill or from ultimate — check each skill&apos;s unlock note.
          </p>
        </>
      )}

      {!loading && !branchLocked && tab === "passives" && passiveTree && (
        <>
          <div className="grid grid-cols-2 gap-3 py-2">
            {passiveTree.passives.map((entry) => (
              <PassiveNode
                key={entry.passive_code}
                entry={entry}
                heroClass={hero.hero_class}
                locked={passiveLocked(entry)}
                onSelect={() => setSelectedPassive(entry)}
              />
            ))}
          </div>
          <p className="text-center text-xs text-gray-500 px-4">
            Passive skills boost your hero permanently. Each level costs {spCostPerUpgrade} skill points.
          </p>
        </>
      )}

      {selected && !branchLocked && (
        <SkillUpgradeModal
          entry={selected}
          heroClass={hero.hero_class}
          heroLevel={hero.level}
          skillPoints={skillPoints}
          gold={balance?.gold ?? 0}
          branch={viewBranch}
          spCostPerUpgrade={spCostPerUpgrade}
          onClose={() => setSelected(null)}
          onUpgraded={() => void loadAll(viewBranch, { silent: true })}
        />
      )}

      {selectedPassive && !branchLocked && (
        <PassiveUpgradeModal
          entry={selectedPassive}
          heroClass={hero.hero_class}
          heroLevel={hero.level}
          skillPoints={skillPoints}
          gold={balance?.gold ?? 0}
          branch={viewBranch}
          spCostPerUpgrade={spCostPerUpgrade}
          onClose={() => setSelectedPassive(null)}
          onUpgraded={() => void loadAll(viewBranch, { silent: true })}
        />
      )}
    </div>
  );
}

function SkillNode({
  entry,
  heroClass,
  locked,
  onSelect,
}: {
  entry: SkillTreeEntry;
  heroClass: import("../types").HeroClass;
  locked: boolean;
  onSelect: () => void;
}) {
  const isMax = entry.current_level >= entry.max_level;
  const label = resolveSkillDisplay(entry.skill_name, { [entry.skill_name]: entry.display_name });

  return (
    <button
      onClick={onSelect}
      className={clsx(
        "relative flex flex-col items-center gap-1 p-1.5 rounded-xl border-2 transition-all w-full max-w-[5.25rem]",
        "bg-kingdom-dark hover:border-kingdom-glow active:scale-95",
        locked ? "border-gray-700 opacity-80" : "border-kingdom-light",
      )}
    >
      {locked && !isMax && (
        <span className="absolute -top-2 -right-2 text-xs bg-gray-800 border border-gray-600 rounded-full w-6 h-6 flex items-center justify-center">
          🔒
        </span>
      )}
      <SkillIcon
        heroClass={heroClass}
        skill={entry.skill_name}
        className="w-12 h-12"
        alt={label}
      />
      <span className="text-[10px] text-gray-300 text-center leading-tight line-clamp-2">{label}</span>
      <span
        className={clsx(
          "text-xs font-bold px-2 py-0.5 rounded-full",
          isMax ? "bg-gold-500/20 text-gold-300" : "bg-kingdom-mid text-kingdom-glow",
        )}
      >
        Lv {entry.current_level}/{entry.max_level}
      </span>
    </button>
  );
}

function PassiveNode({
  entry,
  heroClass,
  locked,
  onSelect,
}: {
  entry: PassiveTreeEntry;
  heroClass: import("../types").HeroClass;
  locked: boolean;
  onSelect: () => void;
}) {
  const isMax = entry.current_level >= entry.max_level;

  return (
    <button
      onClick={onSelect}
      className={clsx(
        "relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all text-center",
        "bg-kingdom-dark hover:border-kingdom-glow active:scale-95",
        locked ? "border-gray-700 opacity-80" : "border-kingdom-light",
      )}
    >
      {locked && !isMax && (
        <span className="absolute top-1 right-1 text-xs">🔒</span>
      )}
      <SkillIcon
        heroClass={heroClass}
        skill={entry.passive_code}
        className="w-12 h-12"
        alt={entry.display_name}
      />
      <span className="font-bold text-sm text-kingdom-glow leading-tight">{entry.display_name}</span>
      <span
        className={clsx(
          "text-xs font-bold px-2 py-0.5 rounded-full mt-1",
          isMax ? "bg-gold-500/20 text-gold-300" : "bg-kingdom-mid text-kingdom-glow",
        )}
      >
        Lv {entry.current_level}/{entry.max_level}
      </span>
    </button>
  );
}
